import base64 from 'base-64'

export const removeEmpty = (obj: Record<string, unknown>): Record<string, unknown> => {
  Object.keys(obj).forEach((key) => {
    const value = obj[key]
    if (value && typeof value === 'object') removeEmpty(value as Record<string, unknown>)
    else if (obj[key] === undefined || obj[key] === null) delete obj[key]
  })
  return obj
}

export const RegExBank = {
  isImage: new RegExp('.(jpeg|jpg|png)$', 'i'),
}

export const FileHelpers = {
  /**
   * Get whether a file is an image (tests file extension)
   * @param {string} strUri - URI to test on.
   * @return {boolean}
   */
  isImage: (strUri: string): boolean => {
    return typeof strUri !== 'string'
      ? false
      : mC.isImage[strUri] || (mC.isImage[strUri] = RegExBank.isImage.test(strUri))
  },

  /**
   * Get the file extension of input string
   * @param {string} strUri - URI to test on.
   * @return {string} - file extension (forced lowercase), '' for any error state
   */
  toFileType: (strUri: string): string => {
    if (typeof mC.toFileType[strUri] !== 'undefined') return mC.toFileType[strUri]
    try {
      const fileName = FileHelpers.toFileName(strUri, true)
      if (!fileName || fileName.lastIndexOf('.') === -1) return (mC.toFileType[strUri] = '')
      const fileExt = fileName.split('.').pop()!.toLowerCase()
      return (mC.toFileType[strUri] = fileExt)
    } catch (_e) {
      return (mC.toFileType[strUri] = '')
    }
  },

  /**
   * Get the file name of input
   * @param {string} strUri - URI to act on.
   * @param {boolean} withExtension - includes extension when true
   * @return {string} - filename, '' for any error state
   */
  toFileName: (strUri: string, withExtension = true): string => {
    const mCKey = String(strUri) + String(withExtension)
    if (typeof mC.toFileName[mCKey] !== 'undefined') return mC.toFileName[mCKey]
    try {
      const fileName = strUri.replace(/\\/g, '/').split('/').pop()
      const fileNameParts = (fileName as string).split('.')
      const fileNameWithoutExt = fileNameParts.shift() as string
      const fileNameUriDecoded = decodeURIComponent(
        withExtension ? (fileName as string) : fileNameWithoutExt
      )
      return (mC.toFileName[mCKey] = fileNameUriDecoded)
    } catch (_e) {
      return (mC.toFileName[mCKey] = '')
    }
  },
}

// Memoization / cache init
type FileHelpersCache = {
  isImage: Record<string, boolean>
  toFileType: Record<string, string>
  toFileName: Record<string, string>
}

const mC = {} as FileHelpersCache
;(Object.keys(FileHelpers) as (keyof FileHelpersCache)[]).forEach((key) => {
  mC[key] = {}
})

// Add ordinal suffix to number
export type NthGender = 'f' | 'm'
export type NthOptions = { gender?: NthGender; skipOther?: boolean }

export const nth = (
  num: number,
  locale: string,
  { gender = 'f', skipOther = false }: NthOptions = {}
) => {
  const j = num % 10
  const k = num % 100

  if (locale === 'fr') {
    if (num === 1 && gender === 'f') return num + 're'
    else if (num === 1 && gender === 'm') return num + 'er'

    if (skipOther) {
      return `${num}`
    }
    return num + 'e'
  } else {
    if (j === 1 && k !== 11) return num + 'st'
    if (j === 2 && k !== 12) return num + 'nd'
    if (j === 3 && k !== 13) return num + 'rd'
    if (skipOther) {
      return `${num}`
    }
    return num + 'th'
  }
}

// export const isImage = (filename) => /\.(jpg|jpeg|png|gif)$/i.test(filename)

export const filenameFromPath = (filename: unknown): string => {
  if (typeof filename !== 'string' || !filename) return ''
  return filename.substring(filename.lastIndexOf('/') + 1)
}

export const validFileTypes = /\.(jpg|jpeg|png|gif|pdf)$/

export type FileInfo = {
  isImage: boolean
  name: string
  extension: string
  isRestrictedFileType: boolean
}

export const getFileInfo = (filename: unknown): FileInfo => {
  const normalizedFile = filenameFromPath(filename)
  const normalizedFilename = normalizedFile.substring(0, normalizedFile.lastIndexOf('.'))
  const normalizedExtension = normalizedFile.substring(normalizedFile.lastIndexOf('.') + 1)
  const isRestrictedFileType = !validFileTypes.test(filename as string)

  return {
    isImage: FileHelpers.isImage(normalizedFile),
    name: normalizedFilename,
    extension: normalizedExtension,
    isRestrictedFileType,
  }
}

export function decodeBase64Token(token: string): Record<string, unknown> {
  try {
    return JSON.parse(base64.decode(token)) as Record<string, unknown>
  } catch (_error) {
    return {}
  }
}

export const toCamelCase = (str: string): string => {
  return str.toLowerCase().replace(/_([a-z])/g, (_match, letter) => letter.toUpperCase())
}
